import { useState, useEffect } from 'react';

import {
  BpkButtonV2,
  SIZE_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import BpkCheckbox from '@skyscanner/backpack-web/bpk-component-checkbox';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import MobilePartnersFilter from '../MobilePartnersFilter';

import type { FilterOptionOnClick } from '../../types';
import type { FilterOption } from '@shared/types/frontend/ubff/hotel-detail-price';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './PartnersFilterOptionList.module.scss';

import { buildOfferFiltersMBDAttributes } from '@hotels-hotel-detail/utils/meaningful-behaviour-data/offer-filters';
import { FILTER_BUTTONS } from '@shared/constants/filter-buttons';
import SelectAllAndClearAll from '@shared/ui/components/SelectAllAndClearAll';
import { withI18n } from '@shared/ui/contexts/i18n';

const cls = cssModules(STYLES);

type PartnersFilterOptionListProps = {
  i18n: I18nService;
  partnerChips: FilterOption[];
  filterOptionOnClick: FilterOptionOnClick;
  isOpen: boolean;
  onClose: (event?: PopStateEvent) => void;
  isMobile: boolean;
  title: string;
};

const PartnersFilterOptionList = ({
  filterOptionOnClick,
  i18n,
  isMobile,
  isOpen,
  onClose,
  partnerChips: initialPartnerChips,
  title,
}: PartnersFilterOptionListProps) => {
  const [partnerChips, setPartnerChips] = useState(initialPartnerChips);
  const [diff, setDiff] = useState<FilterOption[]>([]);
  const [isMaxHeightRemoved, setIsHeightMaxRemoved] = useState(false);

  useEffect(() => {
    const newDiff = partnerChips.filter((partnerChip) => {
      const initialChip = initialPartnerChips.find(
        (initialItem) => initialItem.option === partnerChip.option,
      );
      return initialChip && initialChip.selected !== partnerChip.selected;
    });
    setDiff(newDiff);
  }, [partnerChips, initialPartnerChips]);

  const handleCheckboxChange = (displayName: string) => {
    setPartnerChips((prevChips) =>
      prevChips.map((chip) =>
        chip.displayName === displayName
          ? { ...chip, selected: !chip.selected }
          : chip,
      ),
    );
  };

  const handleSelectAll = () => {
    setPartnerChips((prevChips) =>
      prevChips.map((chip) => ({ ...chip, selected: true })),
    );
    setIsHeightMaxRemoved(true);
  };

  const handleClearAll = () => {
    setPartnerChips((prevChips) =>
      prevChips.map((chip) => ({ ...chip, selected: false })),
    );
  };
  const buildSelectionButton = () => ({
    type: FILTER_BUTTONS.SELECT_ALL,
    label: i18n.translate('Filters_label_select_all'),
    onClick: () => {
      handleSelectAll();
    },
    disabled: partnerChips.every((item) => item.selected),
  });

  const buildClearAllButton = () => ({
    type: FILTER_BUTTONS.CLEAR_ALL,
    label: i18n.translate('Filters_label_clear_all'),
    onClick: () => {
      handleClearAll();
    },
    disabled: partnerChips.every((item) => !item.selected),
  });

  const footer = (
    <BpkButtonV2
      onClick={filterOptionOnClick(diff, 'OPTION_KEY_PROVIDER')}
      className={cls('PartnersFilterOptionList__resultBtn')}
      size={isMobile ? SIZE_TYPES.large : SIZE_TYPES.small}
    >
      <BpkText
        tagName="span"
        className={cls('PartnersFilterOptionList__text')}
        textStyle={TEXT_STYLES.label1}
      >
        {i18n.translate('Button_showResults')}
      </BpkText>
    </BpkButtonV2>
  );

  return isMobile ? (
    <MobilePartnersFilter
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      clearAllButton={buildClearAllButton()}
      partnerChips={partnerChips}
      footer={footer}
      handleCheckboxChange={handleCheckboxChange}
    />
  ) : (
    <div
      className={cls(
        'PartnersFilterOptionList__container',
        isMaxHeightRemoved && 'PartnersFilterOptionList__removeMaxHeight',
      )}
    >
      <div className={cls('PartnersFilterOptionList__action')}>
        <SelectAllAndClearAll
          selectAllButton={buildSelectionButton()}
          clearAllButton={buildClearAllButton()}
        />
      </div>
      <div className={cls('PartnersFilterOptionList__content')}>
        {partnerChips.map(({ displayName, selected }) => (
          <div
            key={displayName}
            role="button"
            tabIndex={0}
            className={cls('PartnersFilterOptionList__option')}
          >
            <BpkCheckbox
              id={displayName}
              name={displayName}
              data-test-id={`partners_filter_${displayName}`}
              onChange={() => {
                handleCheckboxChange(displayName);
              }}
              label={displayName}
              aria-label={displayName}
              checked={selected}
              {...buildOfferFiltersMBDAttributes(displayName)}
            />
          </div>
        ))}
      </div>
      <div className={cls('PartnersFilterOptionList__footer')}>{footer}</div>
    </div>
  );
};

export default withI18n(PartnersFilterOptionList);
